import {
  createContext, useCallback, useContext, useMemo, useReducer, useRef, useState,
  type ReactNode,
} from "react";
import type { ChapterId, ChapterStatus, TourContext, TourMode, TourStep } from "./types";
import { CHAPTERS, CHAPTER_ORDER } from "./chapters";
import { readStatus, writeStatus, readNext, writeNext } from "./progress";
import { useAuth } from "../auth/AuthContext";

export type TourState =
  | { active: false }
  | {
      active: true;
      chapterId: ChapterId;
      steps: TourStep[];
      stepIndex: number;
      mode: TourMode;
    };

export interface ChapterHooks { onStart?: () => void; context?: TourContext }

export interface TourContextValue {
  state: TourState;
  start(chapterId: ChapterId, mode: TourMode): void;
  next(): void;
  back(): void;
  skip(): void;
  dropStep(stepId: string): void;
  registerChapterHook(chapterId: ChapterId, hooks: ChapterHooks): () => void;
  statusOf(chapterId: ChapterId): ChapterStatus | undefined;
  /** The chapter armed to auto-start on its page's next visit (set by Done on
   *  a chapter's last step, cleared by Skip tour), or null. */
  nextOf(): ChapterId | null;
  clearNext(): void;
  setChapterContext(chapterId: ChapterId, ctx: TourContext): void;
  contextOf(chapterId: ChapterId): TourContext | null;
  contextVersion: number;
}

type Action =
  | { type: "START"; chapterId: ChapterId; mode: TourMode; steps: TourStep[] }
  | { type: "NEXT" }
  | { type: "BACK" }
  | { type: "END" }
  | { type: "DROP"; stepId: string };

const INACTIVE: TourState = { active: false };

export function reduce(state: TourState, action: Action): TourState {
  switch (action.type) {
    case "START":
      if (state.active) return state;
      return {
        active: true,
        chapterId: action.chapterId,
        mode: action.mode,
        steps: action.steps,
        stepIndex: 0,
      };
    case "NEXT":
      if (!state.active) return state;
      return { ...state, stepIndex: Math.min(state.stepIndex + 1, state.steps.length - 1) };
    case "BACK":
      if (!state.active) return state;
      return { ...state, stepIndex: Math.max(state.stepIndex - 1, 0) };
    case "END":
      return INACTIVE;
    case "DROP": {
      if (!state.active) return state;
      const i = state.steps.findIndex((s) => s.id === action.stepId);
      if (i === -1) return state;
      const steps = state.steps.filter((s) => s.id !== action.stepId);
      if (steps.length === 0) return INACTIVE;
      // A drop before the cursor shifts it back; a drop at or after it leaves
      // the same index pointing at the following step, clamped to the end.
      const stepIndex = Math.min(
        i < state.stepIndex ? state.stepIndex - 1 : state.stepIndex,
        steps.length - 1,
      );
      return { ...state, steps, stepIndex };
    }
  }
}

const INERT: TourContextValue = {
  state: INACTIVE,
  start: () => {},
  next: () => {},
  back: () => {},
  skip: () => {},
  dropStep: () => {},
  registerChapterHook: () => () => {},
  statusOf: () => undefined,
  nextOf: () => null,
  clearNext: () => {},
  setChapterContext: () => {},
  contextOf: () => null,
  contextVersion: 0,
};

// Named distinctly from the `TourContext` *type* imported above (hands-on
// step predicate input) to avoid shadowing it with this React context object.
const TourReactContext = createContext<TourContextValue>(INERT);

export function TourProvider({ children }: { children: ReactNode }): JSX.Element {
  const reviewerId = useAuth().reviewerId ?? null;
  const [state, dispatch] = useReducer(reduce, INACTIVE);
  const stateRef = useRef(state);
  // Re-syncs to the committed value each render, which is the same
  // accumulated result `send` below already produced synchronously.
  stateRef.current = state;
  const hooksRef = useRef(new Map<ChapterId, ChapterHooks>());

  const contextsRef = useRef(new Map<ChapterId, TourContext>());
  const [contextVersion, setContextVersion] = useState(0);

  // Accept a context only when something the predicates can see has changed.
  // The page calls this from an effect on every store change; an unconditional
  // bump here would re-render the page (a consumer) and loop.
  const setChapterContext = useCallback((chapterId: ChapterId, ctx: TourContext): void => {
    const prev = contextsRef.current.get(chapterId);
    if (prev !== undefined
      && prev.annotations === ctx.annotations && prev.document === ctx.document) return;
    contextsRef.current.set(chapterId, ctx);
    setContextVersion((v) => v + 1);
  }, []);

  const contextOf = useCallback(
    (chapterId: ChapterId): TourContext | null => contextsRef.current.get(chapterId) ?? null,
    [],
  );

  // React 18 batches same-tick dispatches: `state` (and thus `stateRef`, if
  // only ever set from render) does not reflect a dispatch already queued
  // earlier in the same batch. Every guard below reads `stateRef.current`
  // to decide what to do next, so it must stay in lockstep with the queue,
  // not just with the last commit — hence updating it here, synchronously,
  // on every dispatch.
  const send = useCallback((action: Action): void => {
    stateRef.current = reduce(stateRef.current, action);
    dispatch(action);
  }, []);

  const statusOf = useCallback(
    (chapterId: ChapterId) => readStatus(reviewerId, chapterId),
    [reviewerId],
  );

  // A status is written once per chapter, the first time it ends; a replay
  // of an already-seen chapter never changes what was recorded. The hand-off
  // chain is independent of status: Done arms the next chapter to start on
  // its page whatever its history, Skip tour disarms.
  const finish = useCallback(
    (chapterId: ChapterId, status: ChapterStatus): void => {
      if (readStatus(reviewerId, chapterId) === undefined) {
        writeStatus(reviewerId, chapterId, status);
      }
      if (status === "completed") {
        const i = CHAPTER_ORDER.indexOf(chapterId);
        writeNext(reviewerId, CHAPTER_ORDER[i + 1] ?? null);
      } else {
        writeNext(reviewerId, null);
      }
      send({ type: "END" });
    },
    [reviewerId, send],
  );

  const nextOf = useCallback((): ChapterId | null => readNext(reviewerId), [reviewerId]);
  const clearNext = useCallback((): void => { writeNext(reviewerId, null); }, [reviewerId]);

  const start = useCallback((chapterId: ChapterId, mode: TourMode): void => {
    if (stateRef.current.active) return;
    hooksRef.current.get(chapterId)?.onStart?.();
    send({ type: "START", chapterId, mode, steps: [...CHAPTERS[chapterId].steps] });
  }, [send]);

  const next = useCallback((): void => {
    const s = stateRef.current;
    if (!s.active) return;
    if (s.stepIndex >= s.steps.length - 1) finish(s.chapterId, "completed");
    else send({ type: "NEXT" });
  }, [finish, send]);

  const back = useCallback((): void => { send({ type: "BACK" }); }, [send]);

  const skip = useCallback((): void => {
    const s = stateRef.current;
    if (!s.active) return;
    finish(s.chapterId, "skipped");
  }, [finish]);

  const dropStep = useCallback((stepId: string): void => {
    const s = stateRef.current;
    if (!s.active) return;
    const i = s.steps.findIndex((x) => x.id === stepId);
    if (i === -1) return;
    const wasCurrentLast = i === s.stepIndex && i === s.steps.length - 1;
    if (wasCurrentLast) finish(s.chapterId, "completed");
    else send({ type: "DROP", stepId });
  }, [finish, send]);

  const registerChapterHook = useCallback(
    (chapterId: ChapterId, hooks: ChapterHooks): (() => void) => {
      hooksRef.current.set(chapterId, hooks);
      return () => {
        if (hooksRef.current.get(chapterId) === hooks) hooksRef.current.delete(chapterId);
      };
    },
    [],
  );

  const value = useMemo<TourContextValue>(
    () => ({
      state, start, next, back, skip, dropStep, registerChapterHook, statusOf,
      setChapterContext, contextOf, contextVersion, nextOf, clearNext,
    }),
    [
      state, start, next, back, skip, dropStep, registerChapterHook, statusOf,
      setChapterContext, contextOf, contextVersion, nextOf, clearNext,
    ],
  );
  return <TourReactContext.Provider value={value}>{children}</TourReactContext.Provider>;
}

export function useTour(): TourContextValue {
  return useContext(TourReactContext);
}
